import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useWebRTC } from '../../context/WebRTCContext';
import { startGame, kickPlayer } from '../../services/firebase/games';
import { CHATTERS_ID } from '../../constants/game';
import { logError } from '../../utils/logger';
import PlayerVideoCard, { KickDialog } from '../../components/PlayerVideoCard';
import { getGridCols } from '../../utils/videoGrid';

export default function WaitingRoom({ game, isHost, isChatter, chatterProfiles, playerAvatars = {} }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const webrtc = useWebRTC();

  const [starting, setStarting] = useState(false);
  const [showPlayerCode, setShowPlayerCode] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedChatterCode, setCopiedChatterCode] = useState(false);
  const [kickTarget, setKickTarget] = useState(null);
  const [kicking, setKicking] = useState(false);
  const [localMutes, setLocalMutes] = useState({});
  const [localVolumes, setLocalVolumes] = useState({});
  const [localHidden, setLocalHidden] = useState({});

  function handleCopyCode() {
    navigator.clipboard.writeText(game.code).then(() => {
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    });
  }

  function handleCopyChatterCode() {
    navigator.clipboard.writeText(game.chatterCode).then(() => {
      setCopiedChatterCode(true);
      setTimeout(() => setCopiedChatterCode(false), 2000);
    });
  }

  async function handleKickConfirm() {
    if (!kickTarget) return;
    setKicking(true);
    try {
      await kickPlayer(game.id, kickTarget.uid);
    } catch (err) {
      logError(err);
    } finally {
      setKicking(false);
      setKickTarget(null);
    }
  }

  async function handleStart() {
    setStarting(true);
    try {
      await startGame(game.id);
    } catch (err) {
      logError(err);
      setStarting(false);
    }
  }

  const players = Object.entries(game.playerNames || {}).filter(([uid]) => uid !== CHATTERS_ID);
  const chatterList = Object.values(chatterProfiles || {});
  const regularPlayerCount = players.length;

  // ── Chatter view (unchanged) ────────────────────────────────────────────────
  if (isChatter) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-12">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center">
            <div className="text-4xl mb-3" aria-hidden="true">💬</div>
            <h2 className="text-2xl font-bold t-text">{t('waitingRoom.youreIn')}</h2>
            <p className="t-text-muted mt-1 text-sm">{t('waitingRoom.joinedAsChat')}</p>
          </div>

          <div className="t-bg-surface t-border border rounded-2xl p-5">
            <h3 className="text-sm font-medium t-text-muted mb-3">
              {t('waitingRoom.players', { count: regularPlayerCount })}
            </h3>
            <ul className="space-y-2 mb-4">
              {players.map(([uid, name]) => (
                <li key={uid} className="flex items-center gap-3 t-text text-sm">
                  <span className="w-2 h-2 rounded-full bg-(--c-success) shrink-0" aria-hidden="true" />
                  <span className="sr-only">{t('waitingRoom.playerOnline')}</span>
                  {name}
                  {uid === game.hostId && (
                    <span className="ml-auto text-xs t-text-accent font-medium">{t('waitingRoom.host')}</span>
                  )}
                </li>
              ))}
            </ul>

            {game.chattersEnabled && (
              <div className="t-border border-t pt-3">
                <h3 className="text-sm font-medium t-text-muted mb-2">
                  {t('waitingRoom.chat', { count: chatterList.length })}
                </h3>
                {chatterList.length === 0 ? (
                  <p className="text-xs t-text-disabled">{t('waitingRoom.noChattersYet')}</p>
                ) : (
                  <ul className="flex flex-wrap gap-1.5">
                    {chatterList.map((c) => (
                      <li key={c.uid}>
                        <span className="text-xs t-bg-warning t-text-warning px-2 py-0.5 rounded-full">
                          {c.username}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── In-person mode: original text layout ───────────────────────────────────
  if (game.inPersonMode) {
    return (
      <>
        {kickTarget && (
          <KickDialog
            name={kickTarget.name}
            onConfirm={handleKickConfirm}
            onCancel={() => setKickTarget(null)}
            kicking={kicking}
          />
        )}
        <div className="flex-1 flex flex-col items-center justify-center px-4 py-12">
          <div className="w-full max-w-md space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold t-text">{t('waitingRoom.waitingForPlayers')}</h2>
              <p className="t-text-muted mt-1 text-sm">{t('waitingRoom.shareCode')}</p>
            </div>

            <RoomCodes
              game={game}
              showPlayerCode={showPlayerCode}
              setShowPlayerCode={setShowPlayerCode}
              copiedCode={copiedCode}
              handleCopyCode={handleCopyCode}
              copiedChatterCode={copiedChatterCode}
              handleCopyChatterCode={handleCopyChatterCode}
              t={t}
            />

            <div className="t-bg-surface t-border border rounded-2xl p-5 space-y-3">
              <p className="text-xs t-text-muted">{t('waitingRoom.howToPlay')}</p>
            </div>

            <div className="t-bg-surface t-border border rounded-2xl p-5">
              <h3 className="text-sm font-medium t-text-muted mb-3">
                {t('waitingRoom.players', { count: regularPlayerCount })}
              </h3>
              <ul className="space-y-2">
                {players.map(([uid, name]) => (
                  <li key={uid} className="flex items-center gap-3 t-text">
                    <span className="w-2 h-2 rounded-full bg-(--c-success) shrink-0" aria-hidden="true" />
                    <span className="sr-only">{t('waitingRoom.playerOnline')}</span>
                    <span className="flex-1 min-w-0 truncate">{name}</span>
                    {uid === game.hostId ? (
                      <span className="text-xs t-text-accent font-medium shrink-0">{t('waitingRoom.host')}</span>
                    ) : isHost && (
                      <button
                        type="button"
                        onClick={() => setKickTarget({ uid, name })}
                        aria-label={t('players.kick') + ': ' + name}
                        className="text-xs t-text-error hover:opacity-70 shrink-0 t-focus-ring rounded px-1.5 py-0.5 t-bg-input transition-opacity"
                      >
                        {t('players.kick')}
                      </button>
                    )}
                  </li>
                ))}
              </ul>

              {game.chattersEnabled && (
                <div className="mt-4 pt-4 t-border border-t">
                  <h3 className="text-sm font-medium t-text-muted mb-2">
                    {t('waitingRoom.chat', { count: chatterList.length })}
                  </h3>
                  {chatterList.length === 0 ? (
                    <p className="text-xs t-text-disabled">{t('waitingRoom.noChattersShare')}</p>
                  ) : (
                    <ul className="flex flex-wrap gap-1.5">
                      {chatterList.map((c) => (
                        <li key={c.uid}>
                          <span className="text-xs t-bg-warning t-text-warning px-2 py-0.5 rounded-full">
                            {c.username}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            {isHost ? (
              <div className="space-y-2">
                <button
                  onClick={handleStart}
                  disabled={starting || (!game.chattersEnabled && regularPlayerCount < 2)}
                  className="w-full t-btn-primary disabled:opacity-50 disabled:cursor-not-allowed font-semibold rounded-lg px-4 py-3 transition-colors t-focus-ring"
                >
                  {starting ? t('waitingRoom.starting') : t('waitingRoom.startGame')}
                </button>
                {!game.chattersEnabled && regularPlayerCount < 2 && (
                  <p className="text-xs t-text-disabled text-center">{t('waitingRoom.needMorePlayers')}</p>
                )}
              </div>
            ) : (
              <p className="text-center t-text-disabled text-sm">{t('waitingRoom.waitingForHostToStart')}</p>
            )}
          </div>
        </div>
      </>
    );
  }

  // ── Online mode: video grid layout ─────────────────────────────────────────
  const myUid = user?.uid;
  const {
    remoteStreamsByUid,
    playerMediaStates,
    localCameraStream,
    cameraEnabled,
    micEnabled,
    toggleCamera,
    toggleMic,
  } = webrtc ?? {};

  return (
    <>
      {kickTarget && (
        <KickDialog
          name={kickTarget.name}
          onConfirm={handleKickConfirm}
          onCancel={() => setKickTarget(null)}
          kicking={kicking}
        />
      )}

      <div className="flex-1 flex flex-col min-h-0 overflow-y-auto px-4 py-6">
        <div className="w-full max-w-lg mx-auto space-y-5">

          {/* Title */}
          <div className="text-center">
            <h2 className="text-2xl font-bold t-text">{t('waitingRoom.waitingForPlayers')}</h2>
            <p className="t-text-muted mt-1 text-sm">{t('waitingRoom.shareCode')}</p>
          </div>

          {/* Room codes */}
          <RoomCodes
            game={game}
            showPlayerCode={showPlayerCode}
            setShowPlayerCode={setShowPlayerCode}
            copiedCode={copiedCode}
            handleCopyCode={handleCopyCode}
            copiedChatterCode={copiedChatterCode}
            handleCopyChatterCode={handleCopyChatterCode}
            t={t}
          />

          {/* Player video grid */}
          <div className={`grid gap-2 ${getGridCols(regularPlayerCount)}`}>
            {players.map(([uid, name]) => {
              const isMe = uid === myUid;
              return (
                <PlayerVideoCard
                  key={uid}
                  uid={uid}
                  name={name}
                  avatarEmoji={playerAvatars[uid] ?? '🎵'}
                  isMe={isMe}
                  isViewerChatter={false}
                  mediaState={playerMediaStates?.[uid]}
                  localCameraStream={isMe ? localCameraStream : undefined}
                  remoteStreamsByUid={isMe ? undefined : remoteStreamsByUid?.get(uid)}
                  cameraStreamId="camera"
                  myCameraEnabled={isMe ? cameraEnabled : undefined}
                  myMicEnabled={isMe ? micEnabled : undefined}
                  toggleCamera={isMe ? toggleCamera : undefined}
                  toggleMic={isMe ? toggleMic : undefined}
                  isMuted={localMutes[uid] ?? false}
                  volume={localVolumes[uid] ?? 1}
                  isHidden={localHidden[uid] ?? false}
                  onMuteToggle={!isMe ? () => setLocalMutes((p) => ({ ...p, [uid]: !p[uid] })) : undefined}
                  onVolumeChange={!isMe ? (v) => setLocalVolumes((p) => ({ ...p, [uid]: v })) : undefined}
                  onHideToggle={!isMe ? () => setLocalHidden((p) => ({ ...p, [uid]: !p[uid] })) : undefined}
                  onKick={isHost && !isMe ? () => setKickTarget({ uid, name }) : undefined}
                  renderTopLeft={() => (
                    <div className="flex items-center gap-1">
                      <span className="text-xs font-medium text-white/90 truncate drop-shadow">{name}</span>
                      {uid === game.hostId && (
                        <span className="text-xs t-text-accent font-semibold shrink-0 drop-shadow">
                          {t('waitingRoom.host')}
                        </span>
                      )}
                    </div>
                  )}
                />
              );
            })}
          </div>

          {/* Chatters section */}
          {game.chattersEnabled && (
            <div className="t-bg-surface t-border border rounded-2xl p-4">
              <h3 className="text-sm font-medium t-text-muted mb-2">
                {t('waitingRoom.chat', { count: chatterList.length })}
              </h3>
              {chatterList.length === 0 ? (
                <p className="text-xs t-text-disabled">{t('waitingRoom.noChattersShare')}</p>
              ) : (
                <ul className="flex flex-wrap gap-1.5">
                  {chatterList.map((c) => (
                    <li key={c.uid}>
                      <span className="text-xs t-bg-warning t-text-warning px-2 py-0.5 rounded-full">
                        {c.username}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Start / waiting */}
          {isHost ? (
            <div className="space-y-2">
              <button
                onClick={handleStart}
                disabled={starting || (!game.chattersEnabled && regularPlayerCount < 2)}
                className="w-full t-btn-primary disabled:opacity-50 disabled:cursor-not-allowed font-semibold rounded-lg px-4 py-3 transition-colors t-focus-ring"
              >
                {starting ? t('waitingRoom.starting') : t('waitingRoom.startGame')}
              </button>
              {!game.chattersEnabled && regularPlayerCount < 2 && (
                <p className="text-xs t-text-disabled text-center">{t('waitingRoom.needMorePlayers')}</p>
              )}
            </div>
          ) : (
            <p className="text-center t-text-disabled text-sm">{t('waitingRoom.waitingForHostToStart')}</p>
          )}

        </div>
      </div>
    </>
  );
}

// ── Shared room-code display ─────────────────────────────────────────────────
function RoomCodes({
  game, showPlayerCode, setShowPlayerCode,
  copiedCode, handleCopyCode,
  copiedChatterCode, handleCopyChatterCode,
  t,
}) {
  return (
    <div className={`grid gap-3 ${game.chatterCode ? 'grid-cols-2' : 'grid-cols-1'}`}>
      <div className="t-bg-surface t-border border rounded-2xl p-5 text-center">
        <p className="text-xs font-medium t-text-disabled uppercase tracking-widest mb-2">
          {game.chatterCode ? t('waitingRoom.playerCode') : t('waitingRoom.roomCode')}
        </p>
        {game.chatterCode ? (
          <div className="space-y-2">
            {showPlayerCode ? (
              <button
                type="button"
                onClick={handleCopyCode}
                aria-label={t('game.copyCode')}
                className="text-4xl font-mono font-bold tracking-widest t-text-accent hover:opacity-80 t-focus-ring rounded transition-opacity"
              >
                {game.code}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleCopyCode}
                aria-label={t('game.copyCode')}
                className="text-4xl font-mono font-bold tracking-widest t-text-disabled hover:opacity-70 t-focus-ring rounded transition-opacity select-none"
              >
                ••••••
              </button>
            )}
            <p className="text-xs t-text-warning">{t('waitingRoom.doNotLeak')}</p>
            <div className="flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => setShowPlayerCode((v) => !v)}
                className="text-xs t-text-disabled hover:t-text-muted underline transition-colors t-focus-ring rounded"
              >
                {showPlayerCode ? t('waitingRoom.hide') : t('waitingRoom.showCode')}
              </button>
              <button
                type="button"
                onClick={handleCopyCode}
                aria-label={t('game.copyCode')}
                className="text-xs t-text-muted hover:opacity-80 t-focus-ring rounded px-1"
              >
                {copiedCode ? `✓ ${t('game.copied')}` : '📋'}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={handleCopyCode}
              aria-label={t('game.copyCode')}
              className="text-4xl font-mono font-bold t-text-accent tracking-widest hover:opacity-80 t-focus-ring rounded transition-opacity"
            >
              {game.code}
            </button>
            <button
              type="button"
              onClick={handleCopyCode}
              aria-label={t('game.copyCode')}
              className="text-xs t-text-muted hover:opacity-80 t-focus-ring rounded px-1 self-center"
            >
              {copiedCode ? `✓ ${t('game.copied')}` : '📋'}
            </button>
          </div>
        )}
      </div>

      {game.chatterCode && (
        <div className="t-bg-surface t-border border rounded-2xl p-5 text-center">
          <p className="text-xs font-medium t-text-disabled uppercase tracking-widest mb-2">
            {t('waitingRoom.chatCode')}
          </p>
          <div className="flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={handleCopyChatterCode}
              aria-label={t('waitingRoom.copyChatCode')}
              className="text-4xl font-mono font-bold t-text-warning tracking-widest hover:opacity-80 t-focus-ring rounded transition-opacity"
            >
              {game.chatterCode}
            </button>
            <button
              type="button"
              onClick={handleCopyChatterCode}
              aria-label={t('waitingRoom.copyChatCode')}
              className="text-xs t-text-muted hover:opacity-80 t-focus-ring rounded px-1 self-center"
            >
              {copiedChatterCode ? `✓ ${t('game.copied')}` : '📋'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
